import React from 'react'
import { renderToString } from 'react-dom/server'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// React Router v7 exposes StaticRouter from the core `react-router` package.
// Importing from `react-router-dom/server` fails because that export path does not exist.
import { StaticRouter } from 'react-router'

import { AppRoutesPrerender as AppRoutes } from './pages/index.prerender.jsx'
import { getSeoForPath, OG_IMAGE, SITE_ORIGIN, isPublicRoute } from './lib/seo.js'

function headElementsForPath(pathname) {
  const seo = getSeoForPath(pathname)
  const canonical = seo.canonical || `${SITE_ORIGIN}${pathname}`
  const robots = seo.robots || 'index,follow'
  const description = seo.description || ''
  const title = seo.title || 'TrésoPilot'

  // Only public routes should be indexed.
  const isPublic = isPublicRoute(pathname)

  const elements = new Set([
    { type: 'meta', props: { name: 'description', content: description } },
    { type: 'meta', props: { name: 'robots', content: isPublic ? robots : 'noindex,nofollow' } },
    { type: 'link', props: { rel: 'canonical', href: canonical } },

    // Open Graph
    { type: 'meta', props: { property: 'og:type', content: 'website' } },
    { type: 'meta', props: { property: 'og:url', content: canonical } },
    { type: 'meta', props: { property: 'og:title', content: title } },
    { type: 'meta', props: { property: 'og:description', content: description } },
    { type: 'meta', props: { property: 'og:image', content: OG_IMAGE } },

    // Twitter
    { type: 'meta', props: { name: 'twitter:card', content: 'summary_large_image' } },
    { type: 'meta', props: { name: 'twitter:title', content: title } },
    { type: 'meta', props: { name: 'twitter:description', content: description } },
    { type: 'meta', props: { name: 'twitter:image', content: OG_IMAGE } },
  ])

  return { title, elements }
}

export async function prerender(data) {
  const url = data?.url || '/'

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { refetchOnWindowFocus: false, retry: 0 },
    },
  })

  const appHtml = renderToString(
    <QueryClientProvider client={queryClient}>
      <StaticRouter location={url}>
        <AppRoutes />
      </StaticRouter>
    </QueryClientProvider>
  )

  const head = headElementsForPath(url)

  return {
    html: appHtml,
    head: {
      lang: 'fr',
      ...head,
    },
  }
}